import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '../../../../contexts/I18nContext';
import './ColorPicker.scss';

const ColorPicker = ({
  id,
  title,
  description,
  required = false,
  value,
  onChange,
  onValidationChange,
  disabled = false,
  className = '',
  props = {}
}) => {
  const { t } = useI18n();
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState('');
  const [showPalette, setShowPalette] = useState(false);
  const validationCallbackRef = useRef(onValidationChange);

  // 从props中提取具体属性
  const {
    presetColors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
      '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2',
      '#FFFFFF', '#000000', '#808080', '#FF0000', '#00FF00',
      '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF'
    ],
    allowCustom = true,
    format = 'hex' // hex, rgb, hsl
  } = props;

  const validateColor = useCallback((colorValue) => {
    let valid = true;
    let message = '';

    // 必填验证
    if (required && !colorValue) {
      valid = false;
      message = t('aiToolTemplate.colorPicker.validation.required');
    }
    // 颜色格式验证
    else if (colorValue && format === 'hex' && !/^#[0-9A-Fa-f]{6}$/.test(colorValue)) {
      valid = false;
      message = t('aiToolTemplate.colorPicker.validation.invalidFormat');
    }

    setIsValid(valid);
    setValidationMessage(message);

    if (validationCallbackRef.current) {
      validationCallbackRef.current(valid, message);
    }

    return valid;
  }, [required, format, t]);

  useEffect(() => {
    validationCallbackRef.current = onValidationChange;
  }, [onValidationChange]);

  useEffect(() => {
    validateColor(value);
  }, [value, validateColor]);

  // 处理颜色选择
  const handleColorSelect = (color) => {
    validateColor(color);
    if (onChange) {
      onChange(color);
    }
    setShowPalette(false);
  };

  // 处理自定义颜色输入
  const handleCustomColorChange = (e) => {
    const newColor = e.target.value;
    validateColor(newColor);
    if (onChange) {
      onChange(newColor);
    }
  };

  // 转换颜色格式显示
  const getDisplayColor = (color) => {
    if (!color) return '';
    return color;
  };

  return (
    <div className={`color-picker-container ${className} ${!isValid ? 'invalid' : ''}`}>
      {/* 标题和描述 */}
      <div className="component-header">
        <h3 className="component-title">
          {title}
          {required && <span className="required-indicator">*</span>}
        </h3>
        {description && (
          <p className="component-description">{description}</p>
        )}
      </div>

      {/* 颜色选择区域 */}
      <div className="color-picker-content">
        {/* 当前选中颜色显示 */}
        <div className="current-color-display">
          <div 
            className="color-preview"
            style={{ backgroundColor: value || '#FFFFFF' }}
            onClick={() => !disabled && setShowPalette(!showPalette)}
          >
            {!value && (
              <div className="no-color-indicator" />
            )}
          </div>
          <div className="color-info">
            <span className="color-label">{t('aiToolTemplate.colorPicker.selectedColor')}</span>
            <span className="color-value">{getDisplayColor(value) || t('aiToolTemplate.colorPicker.none')}</span>
          </div>
          <button
            type="button"
            className="palette-toggle"
            onClick={() => !disabled && setShowPalette(!showPalette)}
            disabled={disabled}
          >
            {showPalette ? t('aiToolTemplate.colorPicker.hidePalette') : t('aiToolTemplate.colorPicker.showPalette')}
          </button>
        </div>

        {/* 颜色调色板 */}
        {showPalette && (
          <div className="color-palette">
            {/* 预设颜色 */}
            <div className="preset-colors">
              <h4 className="palette-section-title">{t('aiToolTemplate.colorPicker.presetColors')}</h4>
              <div className="color-grid">
                {presetColors.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`color-option ${value === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorSelect(color)}
                    disabled={disabled}
                    title={color}
                  />
                ))}
              </div>
            </div>

            {/* 自定义颜色输入 */}
            {allowCustom && (
              <div className="custom-color">
                <h4 className="palette-section-title">{t('aiToolTemplate.colorPicker.customColor')}</h4>
                <div className="custom-color-input">
                  <input
                    type="color"
                    value={value || '#FFFFFF'}
                    onChange={handleCustomColorChange}
                    disabled={disabled}
                    className="color-input"
                  />
                  <input
                    type="text"
                    value={value || ''}
                    onChange={handleCustomColorChange}
                    placeholder={t('aiToolTemplate.colorPicker.hexPlaceholder')}
                    disabled={disabled}
                    className="hex-input"
                  />
                </div>
              </div>
            )}

            {/* 清除颜色选项 */}
            <div className="color-actions">
              <button
                type="button"
                className="clear-color-button"
                onClick={() => handleColorSelect('')}
                disabled={disabled}
              >
                {t('aiToolTemplate.colorPicker.clearColor')}
              </button>
            </div>
          </div>
        )}

        {/* 验证错误信息 */}
        {!isValid && validationMessage && (
          <div className="validation-message error">
            {validationMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorPicker;
